// Detail sub-navigation (ui-spec §2.4, dashboard layout): a detail page's tabs are routes, drawn as one tab strip
// under the header. A kind has at most three tabs, the views a user opens; everything else is a section on one of
// them, and its old tab URL redirects there (`/cost` → `/overview#cost`). A product detail layout declares its tabs
// with useDetailNav; tab visibility depends only on the kind and the spec.
import { linkTo } from "@nodus/api";
import { AppLink } from "@nodus/ui";
import * as React from "react";

interface KindTabs {
  primary: string[];
  /** Always empty: a kind's other views are sections, never a More menu. Kept for the navigation state. */
  more: string[];
  /** Kind-specific tab labels (a Job's `results` are its Outputs). */
  labels?: Record<string, string>;
  /** Former tabs and the tab whose section now holds each one. */
  moved?: Record<string, string>;
}

const RUN_LABELS = { overview: "Details", results: "Outputs" };
const to = (tab: string, ids: string[]) => Object.fromEntries(ids.map((id) => [id, tab]));

export const KIND_TABS: Record<string, KindTabs> = {
  Job: { primary: ["logs", "results", "overview"], more: [], labels: RUN_LABELS, moved: { ...to("overview", ["configuration", "attempts", "events", "cost"]), members: "logs" } },
  TrainingJob: { primary: ["overview", "results", "logs"], more: [], labels: RUN_LABELS, moved: { gradings: "results", ...to("overview", ["configuration", "events", "cost"]) } },
  Pipeline: { primary: ["overview", "logs"], more: [], moved: to("overview", ["stages", "events", "cost"]) },
  Sweep: { primary: ["overview"], more: [], moved: to("overview", ["cells", "events", "cost"]) },
  Workspace: {
    primary: ["overview", "logs", "configuration"],
    more: [],
    labels: { configuration: "Details" },
    moved: { files: "overview", ...to("configuration", ["sessions", "events", "cost"]) },
  },
  Sandbox: {
    primary: ["overview", "logs", "details"],
    more: [],
    moved: { ...to("overview", ["terminal", "files"]), ...to("details", ["processes", "ports", "metrics", "events", "cost"]) },
  },
  InferenceEndpoint: { primary: ["overview", "api"], more: [] },
  Agent: { primary: ["overview"], more: [], moved: to("overview", ["runs", "evals", "configuration", "revisions", "sessions", "cost"]) },
  AgentRun: { primary: ["overview", "team", "steps"], more: [], moved: { children: "team", ...to("overview", ["logs", "messages", "outputs", "cost"]) } },
  AgentGroup: { primary: ["overview", "runs"], more: [], moved: { cost: "overview" } },
  Pool: { primary: ["overview", "nodes", "routing"], more: [], moved: to("overview", ["utilization", "approvals", "forecast", "recommendations"]) },
  Function: { primary: ["overview", "calls", "logs"], more: [], moved: to("overview", ["configuration", "cost"]) },
  Volume: { primary: ["overview", "revisions"], more: [], moved: { cost: "overview" } },
  Image: { primary: ["overview", "build"], more: [] },
  Secret: { primary: [], more: [] },
};

/** The tab that now holds a former tab's section, for redirecting its old URL. */
export function movedTab(kind: string, tab: string): string | undefined {
  return KIND_TABS[kind]?.moved?.[tab];
}

const LABELS: Record<string, string> = {
  api: "API",
  cost: "Cost",
};

export function tabLabel(id: string, kind?: string): string {
  return (kind ? KIND_TABS[kind]?.labels?.[id] : undefined) ?? LABELS[id] ?? id[0]!.toUpperCase() + id.slice(1);
}

export interface DetailTab {
  id: string;
  label: string;
  href: string;
}

export interface DetailNavState {
  kind: string;
  name: string;
  active: string;
  primary: DetailTab[];
  more: DetailTab[];
}

/** Builds a kind's tabs; `without` drops tabs the spec does not have (Members on a single-node Job). */
export function detailTabs(
  kind: string,
  name: string,
  scope: { org: string; project: string },
  active: string,
  without: string[] = [],
  basePath?: string,
): DetailNavState {
  const def = KIND_TABS[kind] ?? { primary: ["overview"], more: [] };
  const tab = (id: string): DetailTab => ({ id, label: tabLabel(id, kind), href: basePath ? `${basePath}/${id}` : linkTo({ kind, name }, { ...scope, tab: id }) });
  return {
    kind,
    name,
    active,
    primary: def.primary.filter((t) => !without.includes(t)).map(tab),
    more: def.more.filter((t) => !without.includes(t)).map(tab),
  };
}

const Ctx = React.createContext<{ state?: DetailNavState; set: (s?: DetailNavState) => void }>({ set: () => {} });

export function DetailNavProvider({ children }: { children: React.ReactNode }) {
  const [state, set] = React.useState<DetailNavState | undefined>();
  const value = React.useMemo(() => ({ state, set }), [state]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useDetailNavState(): DetailNavState | undefined {
  return React.useContext(Ctx).state;
}

/** Declares the current detail page's tabs for the tab strip. */
export function useDetailNav(state: DetailNavState | undefined) {
  const { set } = React.useContext(Ctx);
  const key = state ? `${state.kind}/${state.name}/${state.active}/${state.primary.map((t) => t.href).join(",")}/${state.more.map((t) => t.href).join(",")}` : "";
  React.useEffect(() => {
    set(state);
    return () => set(undefined);
    // The key captures everything the strip renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, set]);
}

const tabClass =
  "relative -mb-px inline-flex h-11 shrink-0 items-center gap-1 border-b border-transparent text-ui text-fg-muted hover:text-fg-primary aria-[current=page]:border-fg-primary aria-[current=page]:text-fg-primary";

/** The detail tab strip: the kind's views as tabs, drawn only when there are at least two. */
export function DetailTabStrip() {
  const state = useDetailNavState();
  if (!state || state.primary.length < 2) return null;
  return (
    <nav aria-label="Resource" className="mt-5 flex gap-6 overflow-x-auto border-b border-line [scrollbar-width:none]">
      {state.primary.map((t) => (
        <AppLink key={t.id} href={t.href} className={tabClass} aria-current={t.id === state.active ? "page" : undefined}>
          {t.label}
        </AppLink>
      ))}
    </nav>
  );
}
